import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { HomeIcon, FileIcon, CodeIcon, CardIcon } from "@/components/icons";

const NAV = [
  { to: "/dashboard", label: "Overview", short: "Overview", icon: HomeIcon, end: true, testid: "nav-overview" },
  { to: "/dashboard/sources", label: "Sources", short: "Sources", icon: FileIcon, testid: "nav-sources" },
  { to: "/dashboard/install", label: "Install", short: "Install", icon: CodeIcon, testid: "nav-install" },
  { to: "/dashboard/billing", label: "Plan and billing", short: "Plan", icon: CardIcon, testid: "nav-billing" },
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [billing, setBilling] = useState(null);

  const refresh = useCallback(() => {
    api.listDocuments().then(setDocuments).catch(() => {});
  }, []);

  const refreshBilling = useCallback(() => {
    api.getBilling().then(setBilling).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    refreshBilling();
    const onFocus = () => refreshBilling();
    const onStorage = (e) => e.key === "firstrun_billing_refresh" && refreshBilling();
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh, refreshBilling]);

  const handleLogout = async () => {
    // Leave the protected route BEFORE clearing auth, otherwise the Protected
    // wrapper redirects to /login the moment user becomes false.
    navigate("/", { replace: true });
    await logout();
  };

  const isPro = billing?.plan === "pro";

  return (
    <div className="App min-h-screen text-foreground antialiased selection:bg-accent lg:flex">
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden lg:flex w-[248px] shrink-0 flex-col gap-8 border-r border-border px-4 py-7 sticky top-0 h-screen"
      >
        <div className="pl-2">
          <Logo to="/" />
        </div>
        <nav aria-label="Workspace" className="flex flex-col gap-1 text-[15px] font-medium">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `h-11 px-3.5 flex items-center gap-3 rounded-[10px] transition-colors ${
                  isActive ? "paper-dark text-primary-foreground" : "text-soft hover:bg-foreground/5"
                }`
              }
            >
              <Icon /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="flex-1" />
        <div className="flex flex-col gap-2.5 px-2 text-[13px] text-muted-foreground">
          <span data-testid="current-user-email" className="font-mono text-xs truncate" title={user?.email}>
            {user?.email}
          </span>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="self-start text-foreground font-medium underline"
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header data-testid="app-header" className="lg:hidden h-16 shrink-0 px-5 flex items-center justify-between">
          <Logo to="/" size={28} textClass="text-[26px]" />
          <span
            data-testid="plan-pill"
            className="h-[30px] px-3 flex items-center rounded-full border border-input font-mono text-xs"
          >
            {isPro ? "Pro" : "Free"}
          </span>
        </header>

        <main className="flex-1 min-w-0 px-5 pt-1 pb-24 lg:px-14 lg:pt-10 lg:pb-10">
          <div className="mx-auto w-full max-w-[1200px]">
            <Outlet context={{ documents, refresh, billing, refreshBilling, handleLogout }} />
          </div>
        </main>

        {/* Mobile tab bar */}
        <nav
          aria-label="Workspace"
          className="lg:hidden paper-tex fixed bottom-0 inset-x-0 z-40 h-[76px] border-t border-border grid grid-cols-4 text-xs font-medium"
        >
          {NAV.map(({ to, short, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`mobile-${testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 ${isActive ? "text-foreground" : "text-muted-foreground"}`
              }
            >
              <Icon size={22} /> {short}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
